<template>
  <div class="page">
    <div class="header">
      <button class="back-btn" @click="$router.back()">返回</button>
      <h2>{{provinceName}} · {{cityName}} 足迹</h2>
      <!-- 权限控制：可编辑且不在编辑态时，显示编辑按钮 -->
      <button v-if="canEdit && !isEditing" class="edit-btn" @click="isEditing = true">编辑</button>
      <!-- 编辑态显示取消按钮 -->
      <button v-if="isEditing" class="edit-btn" @click="cancelEdit">取消</button>
    </div>

    <!-- 核心：加入淡入淡出动画 -->
    <transition name="fade" mode="out-in">
     
      <div v-if="!recordId" class="article-view" style="display:flex; align-items:center; justify-content:center; color:#999; font-size: 16px;">
        加载中或数据不存在...
      </div>
 
      <!-- ================= 1. 阅读态（杂志风） ================= -->
      <div v-else-if="!isEditing && recordId" class="article-view" key="view">
        <!-- 封面区 -->
        <div class="article-cover-wrap">
          <img :src="coverImage" class="article-cover" alt="封面" />
          <div class="cover-gradient"></div>
          <div class="cover-title">
            <h1>{{ cityName }}</h1>
            <p>{{ form.arriveTime }} <span v-if="form.leaveTime"> 至 {{ form.leaveTime }}</span></p>
          </div>
        </div>

        <!-- 内容区 -->
        <div class="article-content">
          <!-- 顶部引言（永远随机展示一段唯美散文） -->
          <div class="note-text">
            {{ randomLongText }}
          </div>

          <!-- 杂志网格布局（严格按照设计图重构） -->
          <div class="magazine-grid">
            
            <!-- 左侧列：两张竖版卡片（图片在上，文字在下） -->
            <div class="mag-left">
              <!-- 与我相随 -->
              <div class="mag-card-vertical">
                <img :src="partnerImg" class="mag-vertical-img" alt="与我相随" />
                <div class="mag-vertical-info">
                  <h4>👥 与我相随</h4>
                  <p>{{ form.partner || '未记录同行之人' }}</p>
                </div>
              </div>
              
              <!-- 身心所至 -->
              <div class="mag-card-vertical">
                <img :src="spotsImg" class="mag-vertical-img" alt="身心所至" />
                <div class="mag-vertical-info">
                  <h4>📍 身心所至</h4>
                  <p>{{ form.spots || '未记录打卡地点' }}</p>
                </div>
              </div>
            </div>

            <!-- 右侧列：三张横版卡片（图片在左，文字在右） -->
            <div class="mag-right">
              <!-- 美食 -->
              <div class="mag-card-horizontal">
                <img :src="foodImg" class="mag-horizontal-img" alt="美食" />
                <div class="mag-horizontal-info">
                  <h4>🍜 美食共鉴</h4>
                  <p>{{ form.food || '未记录当地美食' }}</p>
                </div>
              </div>
              
              <!-- 住宿 -->
              <div class="mag-card-horizontal">
                <img :src="hotelImg" class="mag-horizontal-img" alt="住宿" />
                <div class="mag-horizontal-info">
                  <h4>🏠 安营扎寨</h4>
                  <p>{{ form.hotel || '未记录住宿信息' }}</p>
                </div>
              </div>

              <!-- 印记存档 -->
              <div class="mag-card-horizontal note-card">
                <img :src="noteImg" class="mag-horizontal-img" alt="印记" />
                <div class="mag-horizontal-info">
                  <h4>📖 印记存档</h4>
                  <p class="note-preview">{{ form.note || randomShortText }}</p>
                </div>
              </div>
            </div>

          </div>
          <!-- 底部走马灯照片墙 -->
          <div class="marquee-section" v-if="displayPhotos.length > 0">
            <div class="marquee-title"><span></span> 珍贵瞬间 <span></span></div>
            <div class="marquee-container">
              <div class="marquee-content">
                <img 
                  v-for="(img, idx) in marqueePhotos" 
                  :key="idx" 
                  :src="img" 
                  @click="previewImg(img)" 
                  class="marquee-img"
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- ================= 2. 编辑态（原有表单） ================= -->
      <div v-else class="form-wrap" key="edit">
        <div class="form-content">
          <div class="form-row">
            <div class="form-item">
              <label>抵达时间</label>
              <input v-model="form.arriveTime" type="date" />
            </div>
            <div class="form-item">
              <label>离开时间</label>
              <input v-model="form.leaveTime" type="date" />
            </div>
          </div>
          <div class="form-row">
            <div class="form-item">
              <label>与我相随</label>
              <input v-model="form.partner" placeholder="恋人/家人/独自" />
            </div>
            <div class="form-item">
              <label>身心所至</label>
              <input v-model="form.spots" placeholder="景区、打卡地点" />
            </div>
          </div>
          <div class="form-row">
            <div class="form-item">
              <label>美食共鉴</label>
              <input v-model="form.food" placeholder="当地特色美食" />
            </div>
            <div class="form-item">
              <label>安营扎寨</label>
              <input v-model="form.hotel" placeholder="酒店名称" />
            </div>
          </div>
          <div class="form-item note-form-item">
            <label>印记存档 <span class="tip-text">（最多1000字）</span></label>
            <textarea
              v-model="form.note"
              maxlength="1000"
              placeholder="写下旅途感想、难忘的故事"
              @input="autoHeight"
              ref="noteText"
            ></textarea>
            <div class="word-count">{{form.note.length}} / 1000</div>
          </div>
          <div class="form-item">
            <label>珍贵瞬间</label>
            <div class="img-row-wrap">
              <label class="add-img">
                + 添加图片
                <input ref="fileInput" type="file" accept="image/*" multiple style="display:none" @change="chooseImg" />
              </label>
              <div class="img-item" v-for="(item,idx) in showImgList" :key="idx" @click="previewImg(item)">
                <img :src="item" alt="" />
                <div class="del" @click.stop="delImg(idx)">×</div>
              </div>
              <div v-if="imgList.length > 5" class="more-btn" @click="openAllImgModal">
                More
              </div>
            </div>
          </div>
        </div>
        
        <!-- 底部按钮，固定在form-wrap底部 -->
        <div class="btn-row-wrap" v-if="canEdit || canDelete">
          <button class="save-btn" v-if="canEdit" @click="saveData">保存修改</button>
          <button class="del-record-btn" v-if="canDelete" @click="deleteRecord">删除本条</button>
        </div>
      </div>
    </transition>

    <!-- 全部图片弹窗：点击More打开 -->
    <div v-if="showAllImgModal" class="modal-mask" @click.self="closeAllImgModal">
      <div class="modal-box all-img-modal">
        <span class="all-img-close" @click="closeAllImgModal">×</span>
        <h3>全部图片（共 {{ imgList.length }} 张）</h3>
        <div class="all-img-wrap">
          <div class="img-item" v-for="item in pagedImgList" :key="item.idx" @click="previewImg(item.url)">
            <img :src="item.url" alt="" />
            <div class="del" @click.stop="delImg(item.idx)">×</div>
          </div>
        </div>
        <div v-if="imgTotalPages > 1" class="img-pagination">
          <button class="page-btn" :disabled="imgPage === 1" @click="goImgPage(imgPage - 1)">上一页</button>
          <span class="page-info">{{ imgPage }} / {{ imgTotalPages }}</span>
          <button class="page-btn" :disabled="imgPage === imgTotalPages" @click="goImgPage(imgPage + 1)">下一页</button>
        </div>
      </div>
    </div>

    <!-- 大图预览弹窗（原图） -->
    <div v-if="showBigImgModal" class="modal-mask" @click.self="closeBigImgModal">
      <div class="big-img-box">
        <img :src="currentBigImgUrl" alt="原图" />
        <span class="big-close" @click="closeBigImgModal">×</span>
      </div>
    </div>

    <!-- 图片上传全局遮罩与进度条 -->
    <div v-if="uploading" class="upload-mask">
      <div class="upload-box">
        <h3>图片处理中...</h3>
        <el-progress :percentage="uploadPercent" :color="customColor"></el-progress>
        <p class="upload-tip">请勿关闭页面或刷新</p>
      </div>
    </div>

    <!-- 👇 新增：自定义查重提示弹窗 -->
    <div v-if="showDuplicateTip" class="tip-mask" @click.self="showDuplicateTip = false">
      <div class="tip-panel">
        <div class="tip-text">
          <div class="tip-title">温馨提示：</div>
          <div class="tip-desc">你已有 {{ this.form.arriveTime }} 的足迹印记，是否确认保存修改？</div>
        </div>
        <div class="tip-btn-group">
          <button class="tip-btn cancel" @click="showDuplicateTip = false">返回修改</button>
          <button class="tip-btn confirm" @click="confirmDuplicateSave">确认保存</button>
        </div>
      </div>
    </div>

  </div>
</template>

<script>
import request from '@/utils/request'
import { getCurrentUser, getFootprintDetail, updateFootprint, delFootprint, getFootprintList } from '@/api/footprint'

// 引入兜底图片
import defCover1 from '@/assets/defaults/def-cover-1.jpg'
import defCover2 from '@/assets/defaults/def-cover-2.jpg'
import defFood from '@/assets/defaults/def-food.jpg'
import defHotel from '@/assets/defaults/def-hotel.jpg'
import defScene1 from '@/assets/defaults/def-scene-1.jpg'
import defScene2 from '@/assets/defaults/def-scene-2.jpg'

export default {
  name: "Detail",
  data() {
    return {
      isEditing: false, 
      defFood: defFood,
      defHotel: defHotel,
      provinceName: "",
      cityName: "",
      recordId: null,
      form: {
        arriveTime: "", leaveTime: "", partner: "", spots: "", food: "", hotel: "", note: ""
      },
      imgList: [],
      imgMaxCount: 10,
      badWords: ["暴力", "色情", "赌博", "诈骗"],
      showAllImgModal: false,
      showBigImgModal: false,
      showDuplicateTip: false,
      isConfirmingSave: false,
      currentBigImgUrl: "",
      imgPage: 1,
      imgPageSize: 10,
      recordOwner: '',
      recordGroupId: '',
      myGroupsMap: {},
      uploading: false,
      uploadPercent: 0,
      customColor: '#409eff',
      // 默认图池
      defaultImgs: [defCover1, defCover2, defScene1, defScene2],
      longTextPool: [
        '微风拂过山野，时光留驻心间。每一段旅途，都是一场与世界的对话。无论是一人的闲庭信步，还是与亲友的结伴同行，那些印在山河间的足迹，都是生命中最温柔的馈赠。虽然文字不能完整诠释内心的感受，但风景与陪伴，早已胜过了千言万语。',
        '山河远阔，人间烟火。每一次出发，都是为了寻找内心深处的宁静与感动。也许照片会泛黄，也许记忆会模糊，但这段共同走过的路，会永远刻在爱的印记里。哪怕此刻仅有只言片语，那些美好的瞬间也已随风入梦。',
        '行过万里路，看遍四季景。旅途的意义，或许不在于终点，而在于沿途看过的风景，和那个陪你一起看风景的人。这刻下印记的一页，留给岁月，也留给未来。愿每一次回忆，都能让心底泛起温暖的涟漪。'
      ],
      randomLongText: '',
      shortTextPool: [
        '这段旅程，在心底悄悄留下了印记...',
        '未写下文字，但风景已铭记于心。',
        '千言万语，都化作了山河间的微风。',
        '让美好的瞬间，永远定格在此刻。'
      ],
      randomShortText: ''
    };
  },
  computed: {
    showImgList() { return this.imgList.slice(0, 5) },
    pagedImgList() {
      const start = (this.imgPage - 1) * this.imgPageSize
      return this.imgList.slice(start, start + this.imgPageSize).map((url, i) => ({ url, idx: start + i }))
    },
    imgTotalPages() { return Math.ceil(this.imgList.length / this.imgPageSize) || 1 },
    currentPhone() {
      const u = JSON.parse(localStorage.getItem('currentUser') || '{}')
      return u.phone || ''
    },
    canEdit() {
      if (this.recordOwner === this.currentPhone) return true
      if (this.recordGroupId && this.myGroupsMap[this.recordGroupId]) return true
      return false
    },
    canDelete() {
      if (this.recordOwner === this.currentPhone) return true
      if (!this.recordGroupId) return false
      const g = this.myGroupsMap[this.recordGroupId]
      return g && g.masterAccount === this.currentPhone
    },
    
    // 图片填充逻辑：优先取用户上传的图，不够就用预设图顶替
    coverImage() {
      if (this.imgList.length > 0) return this.imgList[0]
      return this.defaultImgs[Math.floor(Math.random() * 2)]
    },
    partnerImg() {
      if (this.imgList.length > 3) return this.imgList[3];
      if (this.imgList.length > 0) return this.imgList[0];
      return defScene1; 
    },
    spotsImg() {
      if (this.imgList.length > 4) return this.imgList[4];
      if (this.imgList.length > 0) return this.imgList[0];
      return defScene2; 
    },
    foodImg() {
      if (this.imgList.length > 0) return this.imgList[0];
      return this.defFood;
    },
    hotelImg() {
      if (this.imgList.length > 1) return this.imgList[1];
      if (this.imgList.length === 1) return this.imgList[0];
      return this.defHotel;
    },
    noteImg() {
      if (this.imgList.length > 2) return this.imgList[2];
      if (this.imgList.length > 0) return this.imgList[0];
      return this.defaultImgs[0]; 
    },

    displayPhotos() {
      let photos = [...this.imgList]
      if (photos.length < 4) {
        photos = photos.concat(this.defaultImgs)
      }
      return photos
    },
    marqueePhotos() {
      return [...this.displayPhotos, ...this.displayPhotos]
    }
  },
  async mounted() {
    this.randomLongText = this.longTextPool[Math.floor(Math.random() * this.longTextPool.length)];
    this.provinceName = this.$route.query.province || "";
    this.cityName = this.$route.query.city || "";
    this.recordId = this.$route.query.id ? Number(this.$route.query.id) : null;
    try {
      const user = await getCurrentUser()
      this.imgMaxCount = user.imgMaxCount || 10
    } catch (err) { this.imgMaxCount = 10 }
    try {
      const { getMyGroupList } = await import('@/api/footprint')
      const list = await getMyGroupList()
      const map = {}
      ;(list || []).forEach(g => { map[g.groupId] = g })
      this.myGroupsMap = map
    } catch (e) { this.myGroupsMap = {} }
    await this.loadGroupRecord();
  },
  methods: {
    autoHeight() {
      const el = this.$refs.noteText
      if (el) {
        el.style.height = 'auto'
        el.style.height = el.scrollHeight + 'px'
      }
    },
    async loadGroupRecord() {
      if (!this.recordId) return;
      try {
        // 👇 改为请求单条详情，不再拉全量
        const target = await getFootprintDetail(this.recordId);
        if (!target) {
          alert("未找到这条足迹记录或无权查看");
          this.recordId = null;
          return;
        }
        this.recordOwner = target.createUserId || ''
        this.recordGroupId = target.belongGroupId || ''
        this.provinceName = target.province
        this.cityName = target.city
        this.form = {
          arriveTime: target.arriveTime || "", leaveTime: target.leaveTime || "",
          partner: target.partner || "", spots: target.spots || "",
          food: target.food || "", hotel: target.hotel || "", note: target.note || ""
        }
        this.imgList = Array.isArray(target.photoList) ? target.photoList : [];
        if (!this.form.note) {
          this.randomShortText = this.shortTextPool[Math.floor(Math.random() * this.shortTextPool.length)];
        }
      } catch (e) {
        console.error(e); alert("加载失败");
      }
    },
    cancelEdit() {
      this.isEditing = false
      this.loadGroupRecord()
    },
    async chooseImg(e) {
      const files = Array.from(e.target.files);
      if (!files.length) return;
      if (this.imgList.length + files.length > this.imgMaxCount) {
        alert(`最多上传${this.imgMaxCount}张图片，如需更多图片请联系客服`);
        e.target.value = ''; return;
      }
      this.uploading = true;
      for (let i = 0; i < files.length; i++) {
        try {
          this.uploadPercent = 0;
          const blob = await this.compressImage(files[i], 1920, 0.7);
          const url = await this.uploadToServer(blob, (percent) => { this.uploadPercent = percent; });
          this.imgList.push(url);
        } catch (err) { 
          console.error('上传失败', err);
          alert(err || '图片上传失败，请重试'); 
        }
      }
      this.uploading = false;
      e.target.value = '';
    },
    compressImage(file, maxW, quality) {
      return new Promise((resolve) => {
        const reader = new FileReader();
        reader.readAsDataURL(file);
        reader.onload = (e) => {
          const img = new Image();
          img.src = e.target.result;
          img.onload = () => {
            const canvas = document.createElement('canvas');
            let width = img.width; let height = img.height;
            if (width > maxW) { const scale = maxW / width; width = maxW; height = height * scale; }
            canvas.width = width; canvas.height = height;
            canvas.getContext('2d').drawImage(img, 0, 0, width, height);
            canvas.toBlob(blob => resolve(blob), 'image/jpeg', quality);
          };
        };
      });
    },
    uploadToServer(blob, progressCallback) {
      return new Promise(async (resolve, reject) => {
        try {
          const formData = new FormData();
          formData.append('file', blob, 'photo.jpg');
          const res = await request.post('/uploadImage', formData, {
            headers: { 'Content-Type': 'multipart/form-data' },
            onUploadProgress: (progressEvent) => {
              if (progressCallback && progressEvent.total) {
                progressCallback(Math.round((progressEvent.loaded * 100) / progressEvent.total));
              }
            }
          });
          if (res.success && res.url) resolve(res.url);
          else reject(res.msg || '上传失败');
        } catch (err) { reject(err); }
      });
    },
    delImg(idx) { this.imgList.splice(idx, 1); },
    previewImg(url) { this.currentBigImgUrl = url; this.showBigImgModal = true; },
    closeBigImgModal() { this.showBigImgModal = false; this.currentBigImgUrl = ""; },
    openAllImgModal() { this.imgPage = 1; this.showAllImgModal = true; },
    closeAllImgModal() { this.showAllImgModal = false; },
    goImgPage(p) { if (p < 1 || p > this.imgTotalPages) return; this.imgPage = p; },
    hasBadWord(text) { return this.badWords.some(word => text.includes(word)); },
    async saveData() {
      // ===== 1. 敏感词过滤 =====
      const fields = [
        { label: '与我相随', val: this.form.partner },
        { label: '身心所至', val: this.form.spots },
        { label: '美食共鉴', val: this.form.food },
        { label: '安营扎寨', val: this.form.hotel },
        { label: '印记存档', val: this.form.note }
      ]
      for (const f of fields) {
        if (f.val && this.$hasSensitiveWord(f.val)) {
          this.$showToast(`「${f.label}」包含违规词汇，请修改后重试`); return;
        }
      }

      try {
        // ===== 2. 基础时间校验 =====
        if (!this.form.arriveTime) { alert("抵达时间为必填项"); return; }
        if (this.form.leaveTime && new Date(this.form.leaveTime) < new Date(this.form.arriveTime)) { 
          alert("离开时间不能早于抵达时间！"); return; 
        }
        
        // 👇 拦截未来时间
        const today = new Date();
        const todayStr = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
        if (this.form.arriveTime > todayStr) { alert("抵达时间不能晚于今天"); return; }
        if (this.form.leaveTime && this.form.leaveTime > todayStr) { alert("离开时间不能晚于今天"); return; }

        // ===== 3. 本地敏感词与非法词二次校验 =====
        if (this.hasBadWord(this.form.partner + this.form.spots + this.form.food + this.form.hotel + this.form.note)) { 
          alert("内容包含非法关键词，请修改"); return; 
        }

        // ===== 4. 查重提示 =====
        let allFoots = [];
        try {
          allFoots = await getFootprintList(); // 尝试拉取数据
        } catch (e) {
          console.warn('拉取足迹列表失败，跳过查重', e);
        }
        
        const duplicateRecord = allFoots.find(item => 
          item.createUserId === this.currentPhone && 
          item.arriveTime === this.form.arriveTime &&
          item.id !== this.recordId 
        );

        if (duplicateRecord && !this.isConfirmingSave) {
          this.showDuplicateTip = true;
          return; // 拦截，等待确认
        }

        // ===== 5. 执行保存 =====
        const updatePayload = {
          arriveTime: this.form.arriveTime, leaveTime: this.form.leaveTime,
          partner: this.form.partner, spots: this.form.spots,
          food: this.form.food, hotel: this.form.hotel,
          note: this.form.note, photoList: this.imgList
        }
        
        const result = await updateFootprint(this.recordId, updatePayload)
        if (!result || result.success === false) { 
          alert(result?.msg || "更新失败，可能包含违规内容，请修改！"); 
          return; 
        }

        // 👇 保存成功后重置标记
        this.isConfirmingSave = false;
        
        alert("保存成功！");
        this.isEditing = false;
        await this.loadGroupRecord();
      } catch (err) { 
        alert("存储失败，请重试"); 
      }
    },

    // 👇 新增：确认保存的方法
    confirmDuplicateSave() {
      this.showDuplicateTip = false;
      this.isConfirmingSave = true;
      this.saveData(); // 再次调用保存
    },
    async deleteRecord() {
      const ok = await this.$showConfirm('确定删除本条足迹吗？删除后不可恢复！')
      if (!ok) return
      try {
        const res = await delFootprint(this.recordId)
        if (res && res.success === false) { this.$showToast(res.msg || '删除失败'); return; }
        this.$showToast('已删除', 'success')
        this.$router.replace({ path: '/province', query: { name: this.provinceName } })
      } catch (err) { console.error(err); this.$showToast('删除失败'); }
    },
  }
};
</script>

<style scoped>
/* ============================================================
   1. 页面骨架与 Header（高光玻璃）
   ============================================================ */
.page { 
  padding: 0; 
  background: #f7f8fa; 
  height: 100vh; 
  overflow: hidden; 
  display: flex; 
  flex-direction: column; 
}

.header { 
  display: flex; 
  align-items: center; 
  position: relative; 
  z-index: 10;
  flex-shrink: 0;
  height: clamp(48px, 7vh, 68px); 
  padding: 0 clamp(16px, 3vw, 24px); 
  box-sizing: border-box;
  background: linear-gradient(to bottom, rgba(255, 255, 255, 0.15), rgba(255, 255, 255, 0.05));
  border-bottom: 1px solid rgba(255, 255, 255, 0.2);
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
}

/* 完美返回键 */
.back-btn { 
  height: clamp(24px, 3.2vh, 28px);
  padding: 0 clamp(8px, 1.2vw, 14px);
  border-radius: 6px; 
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(to bottom, rgba(255, 255, 255, 0.6), rgba(255, 255, 255, 0.15));
  color: #fff;
  font-weight: 600;
  border: 1px solid rgba(255, 255, 255, 0.8);
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.1), inset 0 2px 4px rgba(255, 255, 255, 0.9);
  backdrop-filter: blur(8px);
  font-size: clamp(11px, 1.1vw, 14px);
  line-height: 1;
  cursor: pointer;
  flex-shrink: 0;
  transition: all 0.2s ease;
  text-shadow: 0 1px 2px rgba(0,0,0,0.15);
  z-index: 2;
}
.back-btn:hover { background: linear-gradient(to bottom, rgba(255, 255, 255, 0.8), rgba(255, 255, 255, 0.3)); transform: translateY(-1px); }
.back-btn:active { transform: translateY(1px) scale(0.96); box-shadow: inset 0 2px 5px rgba(0, 0, 0, 0.15); }

/* 右侧编辑/取消按钮：高光琉璃胶囊 */
.edit-btn { 
  margin-left: auto; 
  height: clamp(24px, 3.2vh, 28px);
  padding: 0 clamp(8px, 1.2vw, 14px);
  border-radius: 6px; 
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  font-weight: 600;
  font-size: clamp(11px, 1.1vw, 14px);
  border: 1px solid rgba(255, 255, 255, 0.4);
  background: linear-gradient(to bottom, rgba(255,255,255,0.25), rgba(255,255,255,0) 50%), rgba(64, 158, 255, 0.85);
  box-shadow: 0 3px 8px rgba(64, 158, 255, 0.25), inset 0 1px 2px rgba(255, 255, 255, 0.5);
  text-shadow: 0 1px 2px rgba(0,0,0,0.15);
  cursor: pointer;
  z-index: 2; 
}
.edit-btn:hover { transform: translateY(-1px); background: linear-gradient(to bottom, rgba(255,255,255,0.35), rgba(255,255,255,0) 50%), rgba(64, 158, 255, 0.95); }
.edit-btn:active { transform: translateY(1px) scale(0.96); }

/* 居中浮雕标题 */
.header h2 { 
  position: absolute !important;
  left: 50% !important;
  transform: translateX(-50%) !important;
  margin: 0 !important;
  color: #2c3e50 !important; 
  font-size: clamp(15px, 2vw, 20px) !important; 
  font-weight: 900 !important; 
  letter-spacing: 1px !important; 
  white-space: nowrap !important;
  pointer-events: none !important;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 55% !important; 
  line-height: 1.2 !important;
  text-shadow: 
    0 2px 4px rgba(255, 255, 255, 0.9),
    0 4px 12px rgba(255, 255, 255, 0.6),
    0 1px 0px rgba(255, 255, 255, 1) !important;
}

/* 淡入淡出动画 */
.fade-enter-active, .fade-leave-active { transition: opacity 0.3s ease; }
.fade-enter, .fade-leave-to { opacity: 0; }

/* ============================================================
   2. 阅读态（杂志风 + 毛玻璃卡片）
   ============================================================ */
.article-view {
  flex: 1;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  background-image: url("~@/assets/subtle.webp");
  background-repeat: repeat;
  background-size: 500px auto;
  background-color: rgba(247, 243, 233, 0.55);
  padding: clamp(12px, 2vw, 20px);
  box-sizing: border-box;
}

.article-cover-wrap {
  position: relative;
  width: 100%;
  height: clamp(160px, 25vh, 280px); 
  border-radius: 16px;
  overflow: hidden;
  box-shadow: 0 12px 32px rgba(0,0,0,0.15);
  margin-bottom: 20px;
}
.article-cover { width: 100%; height: 100%; object-fit: cover; display: block; }
.cover-gradient { position: absolute; inset: 0; background: linear-gradient(to top, rgba(0,0,0,0.8) 0%, transparent 60%); }
.cover-title { position: absolute; bottom: 20px; left: 24px; color: #fff; z-index: 2; }
.cover-title h1 { 
  margin: 0 0 6px 0; 
  font-size: clamp(22px, 3vw, 30px); 
  font-weight: bold; 
  letter-spacing: 2px; 
  font-family: "STKaiti", "KaiTi", sans-serif; 
  text-shadow: 0 2px 8px rgba(0,0,0,0.6); 
}
.cover-title p { margin: 0; font-size: clamp(12px, 1.2vw, 14px); opacity: 0.9; font-family: "Microsoft YaHei", sans-serif; }

.article-content { max-width: 900px; margin: 0 auto; }

.note-text {
  font-size: clamp(13px, 1.4vw, 15px); 
  line-height: 1.8;
  color: #444;
  text-indent: 2em;
  margin-bottom: clamp(16px, 3vh, 24px); 
  font-family: "Microsoft YaHei", sans-serif;
  white-space: pre-wrap;
}

/* 网格布局 */
.magazine-grid {
  display: grid;
  grid-template-columns: 0.8fr 2fr; 
  gap: clamp(12px, 2vw, 20px);
  margin-bottom: 24px;
  align-items: stretch; 
}

/* 左侧竖向卡片 */
.mag-left { display: flex; flex-direction: column; gap: clamp(12px, 2vw, 20px); }
.mag-card-vertical {
  flex: 1; 
  background: rgba(255, 255, 255, 0.45);
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
  border: 1px solid rgba(255, 255, 255, 0.8);
  border-radius: 16px;
  box-shadow: 0 8px 24px rgba(0,0,0,0.08), inset 0 1px 2px rgba(255,255,255,0.9);
  display: flex;
  flex-direction: column;
  align-items: center; 
  padding: clamp(12px, 2vh, 20px) 0; 
  overflow: hidden;
  min-height: 150px;
  transition: transform 0.2s;
}
.mag-card-vertical:hover { transform: translateY(-2px); }

.mag-vertical-img {
  width: clamp(70px, 10vw, 110px);
  height: clamp(70px, 10vw, 110px);
  object-fit: cover;
  border-radius: 50%; /* 头像式圆形 */
  box-shadow: 0 4px 12px rgba(0,0,0,0.1);
  flex-shrink: 0;
  margin-bottom: 12px;
  border: 3px solid rgba(255,255,255,0.8);
}
.mag-vertical-info {
  flex: 1; 
  width: 100%;
  padding: 0 clamp(8px, 1vw, 12px);
  display: flex;
  flex-direction: column;
  justify-content: flex-start; 
  text-align: center; 
}
.mag-vertical-info h4 { margin: 0 0 6px 0; font-size: clamp(11px, 1vw, 13px); color: #666; font-weight: 600; }
.mag-vertical-info p { margin: 0; font-size: clamp(12px, 1.2vw, 14px); color: #2c3e50; font-weight: 700; line-height: 1.5; word-break: break-all;}

/* 右侧横向卡片 */
.mag-right { 
  display: flex; 
  flex-direction: column;
  gap: clamp(12px, 2vw, 20px); 
  /* 👇 核心：高度 100% 撑满左侧 */
  height: 100%; 
}

/* 右侧卡片通用样式 */
.mag-card-horizontal {
  background: rgba(255, 255, 255, 0.45);
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
  border: 1px solid rgba(255, 255, 255, 0.8);
  border-radius: 16px;
  padding: clamp(10px, 1.5vh, 16px) clamp(12px, 1.5vw, 18px);
  display: flex;
  align-items: center;
  gap: clamp(10px, 1.5vw, 16px);
  box-shadow: 0 8px 24px rgba(0,0,0,0.08), inset 0 1px 2px rgba(255,255,255,0.9);
  min-height: 80px; /* 前两块的保底高度 */
  flex-shrink: 0; /* 前两块不允许被压缩 */
  transition: transform 0.2s;
}
.mag-card-horizontal:hover { transform: translateY(-2px); }

/* 图片 */
.mag-horizontal-img {
  width: clamp(50px, 8vw, 70px);
  height: clamp(50px, 8vw, 70px);
  object-fit: cover;
  border-radius: 12px;
  flex-shrink: 0; 
  box-shadow: 0 2px 8px rgba(0,0,0,0.1);
}

/* 文字容器 */
.mag-horizontal-info { 
  flex: 1; 
  min-width: 0; 
  display: flex;
  flex-direction: column;
  justify-content: center;
}

.mag-horizontal-info h4 { 
  margin: 0 0 4px 0; 
  font-size: clamp(11px, 1vw, 13px); 
  color: #666; 
  font-weight: 600; 
}
.mag-horizontal-info p { 
  margin: 0; 
  font-size: clamp(12px, 1.2vw, 14px); 
  color: #2c3e50; 
  font-weight: 700; 
  line-height: 1.5; 
}
.note-card .mag-horizontal-info {
  height: 100%;
}
.note-card { 
  align-items: flex-start !important; 
  /* 👇 flex: 1 让它自动撑开占满剩下的空间 */
  flex: 1; 
  /* 👇 允许它被极限压缩，防止文字太多撑破父级 */
  min-height: 0; 
}
.note-preview {
  white-space: pre-wrap !important;
  font-weight: normal !important;
  color: #555 !important;
  line-height: 1.6;
  font-size: clamp(12px, 1.1vw, 13px) !important;
  
  /* 👇 填满剩下的文字区域，并在内部滚动 */
  flex: 1;
  overflow-y: auto; 
  padding-right: 6px; 
  -webkit-overflow-scrolling: touch; 
  scrollbar-width: none;
}
.note-preview::-webkit-scrollbar { display: none; }


/* ============================================================
   3. 走马灯照片墙
   ============================================================ */
.marquee-section { margin-top: 10px; margin-bottom: 20px; }
.marquee-title { text-align: center; font-size: 13px; color: #888; margin-bottom: 16px; display: flex; align-items: center; justify-content: center; gap: 12px; font-weight: 600;}
.marquee-title span { display: block; width: 40px; height: 1px; background: rgba(0,0,0,0.1); }
.marquee-container {
  overflow: hidden;
  white-space: nowrap;
  width: 100%;
  mask-image: linear-gradient(to right, transparent, black 10%, black 90%, transparent);
  -webkit-mask-image: linear-gradient(to right, transparent, black 10%, black 90%, transparent);
}
.marquee-content { display: inline-flex; gap: 12px; animation: marquee 25s linear infinite; }
.marquee-container:hover .marquee-content { animation-play-state: paused; }
.marquee-img { height: clamp(80px, 12vh, 120px); width: clamp(80px, 12vh, 120px); object-fit: cover; border-radius: 12px; cursor: pointer; transition: transform 0.2s; box-shadow: 0 4px 12px rgba(0,0,0,0.1); border: 2px solid rgba(255,255,255,0.6);}
.marquee-img:hover { transform: scale(1.05); }
@keyframes marquee { 0% { transform: translateX(0); } 100% { transform: translateX(-50%); } }

@media (max-width: 600px) {
  .magazine-grid { grid-template-columns: 1fr; }
  .mag-left { flex-direction: row; }
  .mag-card-vertical { padding: 12px 0; }
}

/* ============================================================
   4. 编辑态（高光玻璃表单）
   ============================================================ */
.form-wrap { 
  margin: clamp(10px, 2vw, 16px); 
  flex: 1; 
  display: flex; 
  flex-direction: column; 
  overflow: hidden; 
  border-radius: 16px;
  box-shadow: 0 8px 32px rgba(0,0,0,0.08);
  background: rgba(255,255,255,0.3);
  backdrop-filter: blur(12px);
  border: 1px solid rgba(255,255,255,0.8);
}
.form-content { 
  flex: 1; 
  overflow-y: auto; 
  padding: clamp(12px, 2vw, 20px); 
  box-sizing: border-box;
}
.form-item { margin-bottom: clamp(10px, 2vh, 16px); }
.note-form-item { margin-bottom: 0px; }

label { 
  display: block; 
  margin-bottom: clamp(4px, 1vh, 8px); 
  font-size: clamp(12px, 1.2vw, 14px); 
  font-weight: 600; 
  color: #2c3e50; 
}
.tip-text { color: #888; font-size: clamp(10px, 1vw, 12px); font-weight: normal; margin-left: 4px; }

input, textarea { 
  width: 100%; 
  box-sizing: border-box; 
  border: 1px solid rgba(255, 255, 255, 0.8); 
  background: rgba(255, 255, 255, 0.6);
  border-radius: 8px; 
  font-size: clamp(12px, 1.2vw, 14px); 
  color: #333; 
  outline: none;
  transition: all 0.2s;
}
input:focus, textarea:focus {
  border-color: #409eff;
  background: rgba(255, 255, 255, 0.9);
  box-shadow: 0 0 0 2px rgba(64, 158, 255, 0.15);
}
input { height: clamp(30px, 4.5vh, 38px); padding: 0 12px; }
textarea { min-height: clamp(70px, 12vh, 100px); padding: 10px 12px; resize: none; }
.word-count { text-align: right; font-size: 11px; color: #888; margin-top: 4px; }

.img-row-wrap { display: flex; align-items: center; gap: 8px; flex-wrap: nowrap; overflow-x: auto; scrollbar-width: none; padding-bottom: 4px;}
.img-row-wrap::-webkit-scrollbar { display: none; }
.img-item { width: clamp(70px, 10vw, 90px); height: clamp(70px, 10vw, 90px); position: relative; flex-shrink: 0; }
.img-item img { width: 100%; height: 100%; object-fit: cover; border-radius: 8px; border: 1px solid rgba(255,255,255,0.8);}
.del { position: absolute; top: -6px; right: -6px; background: #f56c6c; color: #fff; border-radius: 50%; width: clamp(16px, 2vw, 20px); height: clamp(16px, 2vw, 20px); line-height: clamp(16px, 2vw, 20px); text-align: center; cursor: pointer; z-index: 2; font-size: 12px; box-shadow: 0 2px 6px rgba(245,108,108,0.4);}
.add-img { display: inline-flex; width: clamp(70px, 10vw, 90px); height: clamp(70px, 10vw, 90px); border: 1px dashed rgba(150,150,150,0.5); background: rgba(255,255,255,0.4); border-radius: 8px; align-items: center; justify-content: center; color: #666; cursor: pointer; flex-shrink:0; transition: all 0.2s;}
.add-img:hover { background: rgba(255,255,255,0.8); border-color: #409eff; color: #409eff;}
.add-img input { display: none; }
.more-btn{ width: clamp(70px, 10vw, 90px); height: clamp(70px, 10vw, 90px); border-radius: 8px; border: 1px solid rgba(150,150,150,0.3); background: rgba(255,255,255,0.6); display:flex; align-items:center; justify-content:center; color:#444; font-size: clamp(12px, 1.5vw, 14px); cursor:pointer; flex-shrink:0; font-weight: 600;}

.form-row { display: flex; gap: clamp(8px, 1.5vw, 16px); }
.form-row .form-item { flex: 1; }

/* 底部操作栏（玻璃底座） */
.btn-row-wrap { 
  display: flex; 
  justify-content: center; 
  align-items: center; 
  gap: 16px; 
  height: clamp(50px, 7vh, 64px); 
  flex-shrink:0; 
  background: linear-gradient(to top, rgba(255, 255, 255, 0.3), rgba(255, 255, 255, 0.05));
  border-top: 1px solid rgba(255, 255, 255, 0.8);
  backdrop-filter: blur(12px);
  box-shadow: 0 -4px 20px rgba(0, 0, 0, 0.05);
  padding-bottom: env(safe-area-inset-bottom);
}

.save-btn, .del-record-btn { 
  padding: clamp(4px, 1vh, 8px) clamp(16px, 3vw, 24px); 
  border-radius: 8px; 
  font-size: clamp(13px, 1.5vw, 15px); 
  font-weight: 600;
  cursor: pointer; 
  border: 1px solid rgba(255,255,255,0.3);
  transition: all 0.2s ease;
  text-shadow: 0 1px 2px rgba(0,0,0,0.15);
}

.save-btn { 
  background: linear-gradient(to bottom, rgba(255,255,255,0.25), rgba(255,255,255,0) 50%), rgba(64, 158, 255, 0.85);
  color: #fff;
  box-shadow: 0 4px 12px rgba(64, 158, 255, 0.25), inset 0 1px 2px rgba(255, 255, 255, 0.5);
}
.save-btn:hover { transform: translateY(-1px); background: linear-gradient(to bottom, rgba(255,255,255,0.35), rgba(255,255,255,0) 50%), rgba(64, 158, 255, 0.95); }

.del-record-btn { 
  background: linear-gradient(to bottom, rgba(255,255,255,0.25), rgba(255,255,255,0) 50%), rgba(245, 108, 108, 0.85);
  color: #fff;
  box-shadow: 0 4px 12px rgba(245, 108, 108, 0.25), inset 0 1px 2px rgba(255, 255, 255, 0.5);
}
.del-record-btn:hover { transform: translateY(-1px); background: linear-gradient(to bottom, rgba(255,255,255,0.35), rgba(255,255,255,0) 50%), rgba(245, 108, 108, 0.95); }

.save-btn:active, .del-record-btn:active { transform: translateY(1px) scale(0.96); box-shadow: inset 0 2px 4px rgba(0,0,0,0.2); }

/* ============================================================
   5. 弹窗与遮罩（高光玻璃风）
   ============================================================ */
.modal-mask { 
  position: fixed; inset: 0; 
  background: rgba(0,0,0,0.45); 
  backdrop-filter: blur(4px);
  display: flex; align-items: center; justify-content: center; 
  z-index: 999; 
}
.modal-box { 
  width: clamp(280px, 85vw, 560px); 
  border-radius: 16px; 
  padding: clamp(16px, 3vw, 24px); 
  background-image: url("~@/assets/subtle.webp"); 
  background-repeat: repeat; 
  background-size: 420px auto; 
  background-position: center; 
  background-color: rgba(247, 243, 233, 0.98); 
  box-shadow: 0 20px 50px rgba(0, 0, 0, 0.25), inset 0 1px 2px rgba(255,255,255,1); 
  border: 1px solid rgba(255,255,255,0.8);
  box-sizing: border-box;
}
.all-img-modal { position: relative; max-height: 88vh; display: flex; flex-direction: column; }
.all-img-modal h3 { margin: 0 0 12px 0; font-size: clamp(14px, 1.5vw, 16px); color: #444; text-align: center; font-weight: 600; }
.all-img-close { position: absolute; top: 10px; right: 12px; font-size: 24px; line-height: 1; color: #999; cursor: pointer; padding: 2px 6px; border-radius: 6px; transition: all 0.2s; }
.all-img-close:hover { color: #f56c6c; background: rgba(245,108,108,0.1); }

.all-img-wrap { display: grid; grid-template-columns: repeat(auto-fill, minmax(70px, 1fr)); gap: 10px; margin: 0 0 14px 0; overflow-y: auto; flex: 1; padding-right: 4px; scrollbar-width: none; }
.all-img-wrap::-webkit-scrollbar { display: none; }
.all-img-modal .img-item { width: 100%; aspect-ratio: 1 / 1; position: relative; }
.all-img-modal .img-item img { width: 100%; height: 100%; object-fit: cover; border-radius: 8px; cursor: pointer; border: 1px solid rgba(255,255,255,0.6);}

.img-pagination { display: flex; align-items: center; justify-content: center; gap: 16px; padding-top: 12px; border-top: 1px solid rgba(0,0,0,0.05); }
.page-btn { padding: 4px 12px; border: 1px solid rgba(200,200,200,0.5); border-radius: 6px; background: rgba(255,255,255,0.6); cursor: pointer; font-size: 13px; transition: all 0.2s; }
.page-btn:hover:not(:disabled) { border-color: #409eff; color: #409eff; background: #fff;}
.page-btn:disabled { opacity: 0.4; cursor: not-allowed; }
.page-info { font-size: 13px; color: #666; font-weight: 600;}

.big-img-box{ position:relative; max-width:90vw; max-height:90vh; }
.big-img-box img{ max-width:100%; max-height:90vh; display:block; border-radius: 8px; box-shadow: 0 10px 30px rgba(0,0,0,0.3);}
.big-close{ position:absolute; top:-30px; right:0px; color:#fff; font-size:28px; cursor:pointer; text-shadow: 0 2px 4px rgba(0,0,0,0.5);}

/* 上传遮罩 */
.upload-mask { position: fixed; inset: 0; background: rgba(0, 0, 0, 0.45); backdrop-filter: blur(4px); z-index: 9999; display: flex; align-items: center; justify-content: center; }
.upload-box { background: rgba(255, 255, 255, 0.9); backdrop-filter: blur(20px); padding: clamp(16px, 3vw, 24px); border-radius: 16px; width: clamp(260px, 80vw, 320px); text-align: center; box-shadow: 0 20px 50px rgba(0,0,0,0.15), inset 0 2px 6px rgba(255,255,255,1); border: 1px solid rgba(255,255,255,1); }
.upload-box h3 { margin: 0 0 15px 0; font-size: clamp(15px, 1.5vw, 18px); color: #2c3e50; font-weight: 700;}
.upload-tip { font-size: clamp(11px, 1vw, 13px); color: #888; margin-top: 12px; }
.tip-mask {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.4);
  backdrop-filter: blur(4px);
  z-index: 9999;
  display: flex;
  align-items: center;
  justify-content: center;
}
.tip-panel {
  background-image: url("~@/assets/subtle.webp");
  background-repeat: repeat;
  background-size: 420px auto;
  background-position: center;
  background-color: rgba(247, 243, 233, 0.98);
  border-radius: 16px;
  padding: clamp(20px, 4vw, 32px) clamp(24px, 5vw, 40px);
  box-shadow: 0 20px 50px rgba(0, 0, 0, 0.25), inset 0 1px 2px rgba(255,255,255,1);
  border: 1px solid rgba(255, 255, 255, 0.8);
  text-align: center;
  max-width: clamp(280px, 80vw, 400px);
}
.tip-text {
  margin-bottom: 24px;
}

/* 👇 标题：保持加粗，稍微黑一点 */
.tip-title {
  font-size: clamp(15px, 1.6vw, 17px);
  color: #2c3e50; /* 深蓝灰 */
  font-weight: 700; /* 保持加粗 */
  margin-bottom: 8px; /* 和下方文字拉开一点距离 */
}

/* 👇 正文：正常字体，稍微小一点 */
.tip-desc {
  font-size: clamp(13px, 1.4vw, 15px);
  color: #555; /* 柔和灰 */
  font-weight: normal; /* 正常字体 */
  line-height: 1.6;
}
.tip-btn-group {
  display: flex;
  justify-content: center;
  gap: 16px;
}
.tip-btn {
  padding: clamp(3px, 0.8vh, 5px) clamp(6px, 1vw, 8px);
  border-radius: 8px;
  font-size: clamp(13px, 1.3vw, 15px);
  font-weight: 600;
  cursor: pointer;
  border: 1px solid rgba(255, 255, 255, 0.3);
  transition: all 0.2s;
}
.tip-btn.cancel {
  background: linear-gradient(to bottom, rgba(255,255,255,0.4), rgba(255,255,255,0.1)), rgba(144, 147, 153, 0.85);
  color: #fff;
}
.tip-btn.confirm {
  background: linear-gradient(to bottom, rgba(255,255,255,0.25), rgba(255,255,255,0) 50%), rgba(64, 158, 255, 0.85);
  color: #fff;
}
.tip-btn:hover {
  transform: translateY(-1px);
}
</style>